import React, { MouseEvent, ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { useUserOrganization } from '@modules-deprecated/app/organization/userOrganizationContext'

import { getOrganizationViewUrl } from '../../../../utils/routing/getOrganizationViewUrl'
import { reactRoute } from '../../../../utils/routing/reactRoute'
import { DATA_ACCOUNT_ID, DATA_END_DATE, DATA_START_DATE, formatReportHtml } from '../../utils/formatReportHtml'
import * as Styled from './styles'

interface ReportHtmlProps {
  html?: string
}

export const ReportHtml = ({ html }: ReportHtmlProps): ReactElement => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const navigate = useNavigate()

  const reportHtml = useMemo(() => (html ? formatReportHtml(html, t) : ''), [html, t])

  const goToAccount = useCallback(
    (accountId: string, startDate?: string | null, endDate?: string | null) => {
      const queryParams = {
        sortDirection: 'DESC',
        period: '',
        withVoided: 'false',
      }

      if (!startDate) {
        queryParams.period = 'through:' + endDate
      } else {
        queryParams.period = 'dates:' + startDate + '...' + endDate
      }

      navigate(
        getOrganizationViewUrl(
          organization?.url,
          reactRoute.organization.accounts.getAccountRoute(accountId),
          queryParams,
        ),
      )
    },
    [navigate, organization?.url],
  )

  const handleHtmlClick = useCallback(
    (event: MouseEvent) => {
      const element = event.target as HTMLElement

      if (element?.tagName !== 'A') {
        return
      }

      event.preventDefault()
      const accountId = element.getAttribute(DATA_ACCOUNT_ID)
      const startDate = element.getAttribute(DATA_START_DATE)
      const endDate = element.getAttribute(DATA_END_DATE)

      if (accountId) {
        goToAccount(accountId, startDate, endDate)
      }
    },
    [goToAccount],
  )

  // eslint-disable-next-line @typescript-eslint/naming-convention -- `__html` is the key name React's dangerouslySetInnerHTML requires
  return <Styled.ReportHtmlWrapper onClick={handleHtmlClick} dangerouslySetInnerHTML={{ __html: reportHtml }} />
}
